import type { ItemAttributes, ItemNames } from '@ps-smart-cp/ppfw-client';
import {
  Characters,
  DateRange,
  type DateRangeObject,
  ItemName,
  Locale,
  Numeric,
  NumericRange,
  type NumericRangeObject,
  SingleDate,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  /**
   *  減損評価年月日(抽出条件)
   */
  impairmentValuationDateCondition: DateRangeObject;
  /**
   * 減損累計額
   */
  accumulatedImpairment: string;
  /**
   * 償却計算残存価額
   */
  resdValueAfterImprm: string;
  /**
   * 期首減損累計額
   */
  accumulatedImprmBegin: string;
  /**
   * 当期減損額
   */
  imprmAmtCrPeriod: string;
  /**
   * 減損損失計上額
   */
  impairmentLoss: string;
  /**
   * 減損前耐用年数
   */
  lifeBeforeImpairment: string;
  /**
   * 減損前償却率
   */
  depRateBeforeImprm: string;
  /**
   * 減損前償却月数
   */
  depMonthsBeforeImprm: string;
  /**
   * 減損評価年月日
   */
  impairmentValuationDate: Date | null;
  /**
   * 減損累計額(抽出条件)
   */
  accumulatedImpairmentCondition: NumericRangeObject;
  /**
   * 当期減損額(前月)
   */
  imprmAmtCrPeriodPreviousMonth: string;

  /**
   * 当期減損額(当月累計)
   */
  imprmAmtCrPeriodCrMonth: string;
  /**
   * 償却計算残存率
   */
  resdRateAfterImprm: string;
  /**
   * 減損損失累計額
   */
  accumulatedImprmLossAmt: string;
  /**
   * 減損後帳簿価額
   */
  bookValueAfterImprm: string;
}

const attributes = {
  impairmentValuationDateCondition: DateRange.ymd(),
  accumulatedImpairment: Numeric.preset.currency(),
  resdValueAfterImprm: Numeric.preset.currency(),
  accumulatedImprmBegin: Numeric.preset.currency(),
  imprmAmtCrPeriod: Numeric.preset.currency(),
  impairmentLoss: Numeric.preset.currency(),
  lifeBeforeImpairment: Numeric.of(0, 99, 0),
  depRateBeforeImprm: Numeric.of(0, 1, 3),
  depMonthsBeforeImprm: Numeric.of(0, 9_999, 0),
  impairmentValuationDate: SingleDate.ymd(),
  accumulatedImpairmentCondition: NumericRange.of(0, 9_999_999_999_999, 5),
  imprmAmtCrPeriodPreviousMonth: Characters.preset.freeFormat(),
  imprmAmtCrPeriodCrMonth: Characters.preset.freeFormat(),
  resdRateAfterImprm: Characters.preset.freeFormat(),
  accumulatedImprmLossAmt: Characters.preset.freeFormat(),
  bookValueAfterImprm: Characters.preset.freeFormat(),
} satisfies ItemAttributes<Schema>;

interface Record {
  accumulatedImpairment: string;
  accumulatedImprmBegin: string;
  accumulatedImpairmentFrom: string;
  accumulatedImpairmentTo: string;
  depMonthsBeforeImprm: string;
  depRateBeforeImprm: string;
  impairmentLoss: string;
  impairmentValuationDate: string;
  impairmentValuationDateFrom: string;
  impairmentValuationDateTo: string;
  imprmAmtCrPeriod: string;
  imprmAmtCrPeriodPreviousMonth: string;
  imprmAmtCrPeriodCrMonth: string;
  lifeBeforeImpairment: string;
  resdValueAfterImprm: string;
  resdRateAfterImprm: string;
  accumulatedImprmLossAmt: string;
  bookValueAfterImprm: string;
}

const names = {
  impairmentValuationDateCondition: ItemName.of(
    [Locale.ja, '減損評価年月日'],
    [Locale.en, 'Impairment Valuation Date'],
  ),
  accumulatedImpairment: ItemName.of([Locale.ja, '減損累計額']),
  resdValueAfterImprm: ItemName.of([Locale.ja, '償却計算残存価額']),
  accumulatedImprmBegin: ItemName.of([Locale.ja, '期首減損累計額']),
  imprmAmtCrPeriod: ItemName.of([Locale.ja, '当期減損額']),
  impairmentLoss: ItemName.of([Locale.ja, '減損損失計上額']),
  lifeBeforeImpairment: ItemName.of([Locale.ja, '減損前耐用年数']),
  depRateBeforeImprm: ItemName.of([Locale.ja, '減損前償却率']),
  depMonthsBeforeImprm: ItemName.of([Locale.ja, '減損前償却月数']),
  impairmentValuationDate: ItemName.of([Locale.ja, '減損評価年月日']),
  accumulatedImpairmentCondition: ItemName.of([Locale.ja, '減損累計額']),
  imprmAmtCrPeriodPreviousMonth: ItemName.of([Locale.ja, '当期減損額(前月)']),
  imprmAmtCrPeriodCrMonth: ItemName.of([Locale.ja, '当期減損額(当月累計)']),
  resdRateAfterImprm: ItemName.of([Locale.ja, '償却計算残存率']),
  accumulatedImprmLossAmt: ItemName.of([Locale.ja, '減損損失累計額']),
  bookValueAfterImprm: ItemName.of([Locale.ja, '減損後帳簿価額']),
} satisfies ItemNames<Schema>;

const impairmentInfo = {
  names,
  attributes,
};

interface ImpairmentInfo {
  schema: Schema;
  record: Record;
}

export type { ImpairmentInfo };
export { impairmentInfo };
